'use client';

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileSpreadsheet,
  ArrowRight,
  X,
  Sparkles,
} from 'lucide-react';
import { useCommerce } from '@/context/CommerceContext';

interface HamperGridCell {
  id: number;
  image: string;
  title: string;
  tamilTitle: string;
  category: string;
  desc: string;
  contents: string[];
  price: string;
  moq: string;
  badge: string;
  packagingSpec: string;
}

export default function CorporateAndBulkSection() {
  const { openRfqWithData, hampers } = useCommerce();
  const [selectedCell, setSelectedCell] = useState<HamperGridCell | null>(null);

  const liveGridCells: HamperGridCell[] = React.useMemo(() => {
    if (!hampers || hampers.length === 0) return [];
    const filtered = hampers.filter(
      (h) => h.moduleType === 'corporate' || ['corporate', 'bulk', 'executive', 'vip'].includes(h.occasionCategory)
    );
    const source = filtered.length > 0 ? filtered : hampers;
    return source.slice(0, 9).map((h, i) => ({
      id: i + 1,
      image: h.image || '/images/corporate_grid/cell_1.jpg',
      title: h.title,
      tamilTitle: h.tamilTitle || h.title,
      category: h.packagingType || 'Artisanal Keepsake Trunk',
      desc: h.description,
      contents: h.includedSnacks || [],
      price: `From ₹${h.basePrice} / box`,
      moq: `${h.minOrderQty || 20} boxes`,
      badge: h.badge || 'Executive Edition',
      packagingSpec: h.packagingType || 'Luxury gift packaging with custom branding',
    }));
  }, [hampers]);

  if (liveGridCells.length === 0) {
    return null;
  }

  return (
    <section
      id="corporate-bulk"
      className="w-full p-0 m-0 bg-white select-none overflow-hidden scroll-mt-16 relative"
    >
      {/* ======================================================== */}
      {/* FULL-WIDTH, SEAMLESS 3x3 GRID WITH ZERO GAPS             */}
      {/* ======================================================== */}
      <div className="w-full grid grid-cols-3 gap-0 p-0 m-0">
        {liveGridCells.map((cell) => (
          <div
            key={cell.id}
            onClick={() => setSelectedCell(cell)}
            className="group relative aspect-[3/4] w-full overflow-hidden cursor-pointer bg-slate-100 select-none"
            title={`Click to view ${cell.title}`}
          >
            {/* Edge-to-Edge Seamless Image Tile */}
            <img
              src={cell.image}
              alt={cell.title}
              className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 block"
            />

            {/* Subtle Interactive Hover Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#061A14]/90 via-[#061A14]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-3 sm:p-5 lg:p-7 text-white">
              <span className="text-[10px] sm:text-xs font-semibold text-[#45D1BA] uppercase tracking-wider line-clamp-1">
                {cell.category}
              </span>
              <h4 className="font-display font-bold text-xs sm:text-base lg:text-xl text-[#F7F4D6] line-clamp-1 mt-0.5">
                {cell.title}
              </h4>
              <div className="flex items-center justify-between pt-2 sm:pt-3 border-t border-white/15 mt-2">
                <span className="text-xs sm:text-sm text-white/95 font-semibold">
                  {cell.price}
                </span>
                <span className="inline-flex items-center space-x-1.5 text-xs sm:text-sm font-bold text-[#45D1BA] group-hover:translate-x-1 transition-transform">
                  <span>Inquire</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Action Button Area Below The Grid (Not Overlaying on Images) */}
      <div className="w-full bg-white py-6 sm:py-8 md:py-10 flex items-center justify-center px-4">
        <button
          onClick={() =>
            openRfqWithData({
              orderType: 'CORPORATE',
              boxType: 'Elavarasi Corporate Keepsake Collection',
              estimatedQty: 50,
              notes: 'Corporate gifting inquiry for custom branded keepsake trunks and sliding hampers.',
            })
          }
          className="w-full sm:w-auto px-8 py-4 rounded-full bg-[#061A14] hover:bg-[#0A2E24] text-[#F7F4D6] font-display font-bold text-xs sm:text-sm uppercase tracking-wider transition-all shadow-xl hover:shadow-2xl border border-[#E09F55]/40 flex items-center justify-center space-x-2.5 cursor-pointer group"
        >
          <Sparkles className="w-4 h-4 text-[#45D1BA]" />
          <span>Request Corporate Hamper Quote (MOQ 20+)</span>
          <ArrowRight className="w-4 h-4 text-[#45D1BA] group-hover:translate-x-1 transition-transform" />
        </button>
      </div>

      {/* ======================================================== */}
      {/* DETAIL LIGHTBOX MODAL WITH TAILORED ACTIONS              */}
      {/* ======================================================== */}
      <AnimatePresence>
        {selectedCell && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-lg rounded-3xl overflow-hidden bg-[#09261E] border border-white/20 shadow-2xl p-6 sm:p-8 space-y-5 text-white"
            >
              <button
                onClick={() => setSelectedCell(null)}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="relative aspect-[3/4] w-full max-w-xs mx-auto rounded-2xl overflow-hidden border border-white/20 shadow-xl">
                <img
                  src={selectedCell.image}
                  alt={selectedCell.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="space-y-2 text-center">
                <span className="text-xs font-bold text-[#45D1BA] uppercase tracking-wider">
                  {selectedCell.category} • {selectedCell.badge}
                </span>
                <h3 className="font-display font-bold text-2xl text-[#F7F4D6]">
                  {selectedCell.title}
                </h3>
                <p className="text-xs text-[#E09F55] font-serif">
                  {selectedCell.tamilTitle}
                </p>
                <p className="text-sm text-[#C2D1CC] max-w-md mx-auto leading-relaxed">
                  {selectedCell.desc}
                </p>

                {/* Contents Breakdown */}
                <div className="py-2 flex flex-wrap items-center justify-center gap-2 text-xs">
                  {selectedCell.contents.map((item, i) => (
                    <span
                      key={i}
                      className="px-2.5 py-1 rounded-full bg-white/10 text-white border border-white/10"
                    >
                      {item}
                    </span>
                  ))}
                </div>

                <div className="pt-1 text-xs text-[#E09F55] font-semibold">
                  Price: {selectedCell.price} • Minimum Order: {selectedCell.moq}
                </div>
                <div className="text-[11px] text-slate-400 italic">
                  Packaging: {selectedCell.packagingSpec}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    const item = selectedCell;
                    setSelectedCell(null);
                    openRfqWithData({
                      orderType: 'CORPORATE',
                      boxType: item.title,
                      estimatedQty: parseInt(item.moq) || 25,
                      notes: `Inquiry for ${item.title} (${item.category}). Price: ${item.price}, MOQ: ${item.moq}. Packaging: ${item.packagingSpec}`,
                    });
                  }}
                  className="flex-1 py-3 rounded-full bg-[#45D1BA] hover:bg-[#38bca7] text-[#061A14] font-bold text-xs uppercase tracking-wider transition-all shadow-lg flex items-center justify-center space-x-2 cursor-pointer"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  <span>Request Quote For This Hamper</span>
                </button>
                <button
                  onClick={() => setSelectedCell(null)}
                  className="px-5 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white font-bold text-xs uppercase tracking-wider transition-all border border-white/15 cursor-pointer"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
}
